import React, { ChangeEvent, FC, useContext, useState, SyntheticEvent } from 'react';
import { Order, OrderPatient, PatientCategory } from 'models/order';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { patientInfoTemplate } from 'apollo/templates/order';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { patientId } from 'utils/patient';
import { requiresSwedishPinValidation } from 'utils/personal-identity-number';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { nameRegExp } from 'config/regex';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    TextField,
} from '@mui/material';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type PatientSectionDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order: Order;
};

const PatientSectionDialog: FC<PatientSectionDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
}: PatientSectionDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'patient']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const requiresSwedishPin = requiresSwedishPinValidation(currentOrgVar());
    const [isPinValid, setIsPinValid] = useState<boolean>(false);
    const [shouldNotCheckPin, setShouldNotCheckPin] = useState<boolean>(false);
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const [patient, setPatient] = useState<OrderPatient>({
        firstName: '',
        lastName: '',
        personalIdentityNumber: '',
        noPersonalIdentityNumber: false,
        gender: '',
        category: '',
        patientCategory: {
            minage: 0,
            maxage: 0,
            name: '',
            translatedName: '',
        },
    });
    const patientCategories = customerSettings.getCustomerSetting(
        order?.customer?.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PATIENT_CATEGORIES,
    );
    const genders = ['MALE', 'FEMALE'];

    // Seeded when the dialog opens. A live order update (DD-3231) must not reset what is
    // being typed, and the next open picks up the latest patient.
    useSeedOnOpen(open, () => {
        if (order?.patient) {
            setPatient(order.patient);
            setIsPinValid(true);
            setIsFirstNameValid(!!order.patient.firstName);
            setIsLastNameValid(!!order.patient.lastName);
            setShouldNotCheckPin(order.patient.noPersonalIdentityNumber || false);
        }
    });

    const onPersonalNumberChange = (event: ChangeEvent) => {
        const pin = (event.target as HTMLInputElement).value;
        if (!requiresSwedishPin) {
            // No Swedish validation — free text, flag mirrors whether a PIN was entered
            setIsPinValid(!pin || nameRegExp.test(pin));
            setPatient({ ...patient, personalIdentityNumber: pin, noPersonalIdentityNumber: !pin });
            return;
        }
        if (!shouldNotCheckPin) {
            setIsPinValid(nameRegExp.test(pin));
            setPatient({ ...patient, personalIdentityNumber: pin, noPersonalIdentityNumber: shouldNotCheckPin });
            return;
        }
        setPinValue(pin, shouldNotCheckPin);
    };

    const setPinValue = (pin: string, noPersonalIdentityNumber: boolean) => {
        let isValid = true;
        let personalIdentityNumber = pin || '';

        if (requiresSwedishPin && !noPersonalIdentityNumber) {
            const { isPinValid, pid } = patientId.checkYearPrefix(pin, pin);
            isValid = isPinValid;
            personalIdentityNumber = pid;
        } else if (pin) {
            isValid = nameRegExp.test(pin);
        }

        setIsPinValid(isValid);
        setPatient({ ...patient, personalIdentityNumber, noPersonalIdentityNumber });
    };

    const handleCheckPin = (checked: boolean) => {
        setShouldNotCheckPin(checked);
        if (checked) {
            setIsPinValid(true);
            setPatient({ ...patient, noPersonalIdentityNumber: checked });
        } else {
            const pin = patient.personalIdentityNumber;
            setPinValue(pin, checked);
        }
    };

    const handleFirstNameChange = (event: ChangeEvent) => {
        let value = (event.target as HTMLInputElement).value;
        value = value.charAt(0).toUpperCase() + value.slice(1);
        setIsFirstNameValid(nameRegExp.test(value));
        setPatient({ ...patient, firstName: value });
    };

    const handleLastNameChange = (event: ChangeEvent) => {
        let value = (event.target as HTMLInputElement).value;
        value = value.charAt(0).toUpperCase() + value.slice(1);
        setIsLastNameValid(nameRegExp.test(value));
        setPatient({ ...patient, lastName: value });
    };

    const categorySelect = (event: SelectChangeEvent<string>) => {
        const category = event.target.value;
        const patientCategory = patientCategories.find((cat: PatientCategory) => cat.name === category);
        if (patientCategory) {
            patientCategory.translatedName = translate(`patient:${category}`);
        }
        setPatient({ ...patient, category: category, patientCategory: patientCategory });
    };

    const genderSelect = (event: SelectChangeEvent<string>) => {
        const gender = event.target.value;
        setPatient({ ...patient, gender: gender });
    };

    const [patientInfo] = useMutation(patientInfoTemplate, {
        onError: (err) => {
            console.log('err', err);
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('orders:PATIENT_INFO_NOT_UPDATED'),
            });
        },
        onCompleted: ({ patientInfo }) => {
            patientInfo.version = order.version + 1;
            onUpdate?.({ ...order, ...patientInfo });
            onClose();
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('orders:PATIENT_INFO_UPDATED'),
            });
        },
    });

    const handleSave = async () => {
        await patientInfo({
            variables: {
                orderId: order?._id,
                patient: patient,
            },
        });
    };

    const handleClose = () => {
        if (order.patient) {
            setPatient(order.patient);
            setShouldNotCheckPin(order.patient.noPersonalIdentityNumber || false);
        }
        onClose();
    };

    const showPinError = !isPinValid && (requiresSwedishPin ? !shouldNotCheckPin : true);
    const pinErrorMessage = requiresSwedishPin
        ? translate('common:VALIDATION.INCORRECT_PIN')
        : translate('common:VALIDATION.CONTAINS_INVALID_CHARACTERS');

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={handleClose}
            title={translate('orders:EDIT_PATIENT_INFORMATION')}
            actions={
                <Button
                    disabled={
                        !isPinValid ||
                        !isFirstNameValid ||
                        !isLastNameValid ||
                        (requiresSwedishPin && !shouldNotCheckPin && !patient.personalIdentityNumber) ||
                        !patient.firstName ||
                        !patient.lastName
                    }
                    onClick={handleSave}
                    variant="contained"
                    data-test="patient-section-dialog.save-btn"
                >
                    {translate('common:SAVE')}
                </Button>
            }
        >
            <Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            id="personal-number"
                            fullWidth
                            required={requiresSwedishPin && !shouldNotCheckPin}
                            margin="dense"
                            label={translate('common:PERSONAL_IDENTITY_NUMBER')}
                            helperText={showPinError ? pinErrorMessage : ''}
                            error={showPinError}
                            onChange={onPersonalNumberChange}
                            value={patient.personalIdentityNumber}
                            data-test="patient-section-dialog.personal-identity-number-input"
                        />
                        {requiresSwedishPin ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="no-personal-identity-number"
                                        name="noPersonalIdentityNumber"
                                        color="secondary"
                                        onChange={(_: SyntheticEvent, checked) => handleCheckPin(checked)}
                                        checked={shouldNotCheckPin}
                                        data-test="patient-section-dialog.no-personal-identity-number-checkbox"
                                    />
                                }
                                label={translate('orders:MISSING_PERSONAL_NUMBER')}
                            />
                        ) : undefined}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            id="first-name"
                            fullWidth
                            required
                            margin="dense"
                            label={translate('common:FIRST_NAME')}
                            onChange={handleFirstNameChange}
                            error={!patient.firstName}
                            value={patient.firstName}
                            data-test="patient-section-dialog.first-name-input"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            id="last-name"
                            fullWidth
                            required
                            margin="dense"
                            label={translate('common:LAST_NAME')}
                            onChange={handleLastNameChange}
                            error={!patient.lastName}
                            value={patient.lastName}
                            data-test="patient-section-dialog.last-name-input"
                        />
                    </Grid>
                    {patient.category ? (
                        <Grid item xs={12} md={4}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel required>{translate('patient:PATIENT_CATEGORY')}</InputLabel>
                                <Select
                                    value={patient.category}
                                    label={translate('patient:PATIENT_CATEGORY')}
                                    onChange={categorySelect}
                                    data-test="patient-section-dialog.patient-category-select"
                                >
                                    {patientCategories.map((category: PatientCategory) => (
                                        <MenuItem key={category.name} value={category.name}>
                                            {translate(`patient:${category.name}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : undefined}
                    {patient.gender ? (
                        <Grid item xs={12} md={4}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel required>{translate('patient:GENDER')}</InputLabel>
                                <Select
                                    value={patient.gender}
                                    label={translate('patient:GENDER')}
                                    onChange={genderSelect}
                                    data-test="patient-section-dialog.patient-gender-select"
                                >
                                    {genders.map((gender) => (
                                        <MenuItem key={gender} value={gender}>
                                            {translate(`patient:${gender}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>
        </DHDialog>
    );
};
export default PatientSectionDialog;
